import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { WalletTemplate } from '../../components/Templates';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useGameCurrency from '../../hooks/useGameCurrency';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import {
  ReferralSystem,
  RewardCategory,
  RewardOfferType,
} from '../../models/RewardStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

const WalletScreen = () => {
  const navigate = useNavigate();

  useRedirectEntry();

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const allowance = useUserStore((store) => store.getAllowance());

  const { getCurrencyData, showCurrencyDetailPopup } = useGameCurrency(
    userAccount,
    userProfile,
    allowance,
    false,
  );

  // MARK: GET WEB STORE
  const rewardOfferStore = 'system';
  const allRewardCategoryId =
    RewardCategory.createAll(rewardOfferStore).rewardCategoryId;

  const rewardOffersFieldName = `${rewardOfferStore}_${allRewardCategoryId}`;
  const rewardOffersField = useRewardStoreListField(
    'offers',
    rewardOffersFieldName,
  );
  const fetchRewardOffers = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchOffers(
          rewardOfferStore,
          { referralSystem: ReferralSystem.Joyajoy },
          cursor,
        ),
    [],
  );
  const { fetchAll: fetchAllRewardOffers } = useFetchList(
    rewardOffersField,
    fetchRewardOffers,
  );

  useEffect(() => {
    if (activeUserId) {
      fetchAllRewardOffers().catch(console.error);
    }
  }, [fetchAllRewardOffers, activeUserId]);

  const rewardOffers = useRewardStore(
    (store) => store.getOffers(rewardOffersFieldName),
    arrayEqual(shallow),
  );

  const webStoreRewardOffers = useMemo(() => {
    return rewardOffers.filter((offer) =>
      [
        // RewardOfferType.ReferralRole,
        // RewardOfferType.ClassroomMembershipTierSet,
        // RewardOfferType.WebPlan,
        // RewardOfferType.WeaMembership,
        RewardOfferType.Token,
      ].includes(offer.reward.type),
    );
  }, [rewardOffers]);

  const onClickSubscription = useCallback(() => {
    navigate('/cloud-storage');
  }, [navigate]);

  const onClickRegister = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  const onClickAllWebStore = useCallback(() => {
    navigate('/store');
    window.scrollTo(0, 0);
  }, [navigate]);

  const onClickProduct = useCallback(
    (rewardOfferId: string) => {
      navigate(`/store/${rewardOfferId}`);
      window.scrollTo(0, 0);
    },
    [navigate],
  );

  return (
    <WalletTemplate
      isTrialUser={userProfile?.isTrialUser ?? false}
      tier={userAccount?.activeWebPlanTier}
      webProducts={webStoreRewardOffers}
      getCurrencyData={getCurrencyData}
      onClickSubscription={onClickSubscription}
      onClickCurrency={showCurrencyDetailPopup}
      onClickRegister={onClickRegister}
      onClickAllWebStore={onClickAllWebStore}
      onClickProduct={onClickProduct}
    />
  );
};

export default WalletScreen;
